<template>
  <section class="property-statistics">
    <loading-spinner :loading="loading" />
    <div
      v-if="!loading"
      class="columns is-multiline"
    >
      <div class="column is-half">
        <label class="label">Current Rates</label>
        <chart-container
          ref="rateChart"
          type="line"
          :data="rateData"
          :options="rateOptions"
        />
      </div>

      <div class="column is-half">
        <label class="label">Booking Revenue</label>
        <chart-container
          ref="revenueChart"
          type="bar"
          :data="revenueData"
          :options="revenueOptions"
        />
      </div>

      <div class="column is-half">
        <label class="label">Monthly Occupancy</label>
        <chart-container
          ref="occupancyChart"
          type="bar"
          :data="occupancyData"
          :options="occupancyOptions"
        />
      </div>

      <div class="column is-half">
        <label class="label">Reservation Sources</label>
        <chart-container
          ref="sourceChart"
          type="pie"
          :data="sourceData"
          :options="sourceOptions"
        />
        <!-- Custom legend -->
        <div
          v-if="sourceOptions.legendNames && sourceOptions.legendNames.length"
          class="ct-legend"
        >
          <ul class="ct-legend-items">
            <li
              v-for="(name, index) in sourceOptions.legendNames"
              :key="index"
              :class="`ct-series-${index}`"
            >
              {{ name }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import * as Chartist from 'chartist'
import format from 'date-fns/format'
import isFirstDayOfMonth from 'date-fns/is_first_day_of_month'
import Raven from 'raven-js'
import LoadingSpinner from 'vue-spinner/src/MoonLoader'
import { mapGetters } from 'vuex'
import { currencyCodeToSymbol } from '../helpers/currency-code-to-symbol'
import ApiGateway from '@/services/api-gateway'
import { getErrorMessage } from '@/services/helpers'

// ChartContainer component to replace vue-chartist
const ChartContainer = {
  name: 'ChartContainer',
  
  props: {
    type: {
      type: String,
      required: true,
      validator: value => [
        'line',
        'bar',
        'pie'
      ].includes(value.toLowerCase())
    },
    data: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => ({})
    }
  },
  
  data () {
    return { chart: null }
  },
  
  mounted () {
    this.createChart()
  },
  
  beforeDestroy () {
    if (this.chart !== null && this.chart !== undefined) {
      // Clean up chart instance if needed
      this.chart = null
    }
  },
  
  watch: {
    data: {
      deep: true,
      handler () {
        this.updateChart()
      }
    },
    options: {
      deep: true,
      handler () {
        this.updateChart()
      }
    }
  },
  
  methods: {
    createChart () {
      let ChartConstructor
      
      // In Chartist 1.3.0, constructors are directly on the Chartist object
      switch (this.type.toLowerCase()) {
        case 'line':
          ChartConstructor = Chartist.LineChart
          break
        case 'bar':
          ChartConstructor = Chartist.BarChart
          break
        case 'pie':
          ChartConstructor = Chartist.PieChart
          break
        default:
          console.error('Unknown chart type:', this.type)
          return
      }
      
      if (ChartConstructor === null || ChartConstructor === undefined) {
        console.error('Chartist constructor not found for type:', this.type)
        return
      }
      
      this.chart = new ChartConstructor(
        this.$el,
        this.data,
        this.options
      )
    },
    
    updateChart () {
      if (this.chart !== null && this.chart !== undefined) {
        this.chart.update(this.data, this.options)
      }
    }
  },
  
  render (h) {
    return h('div', {
      class: {
        'ct-chart': true,
        [`ct-chart-${this.type.toLowerCase()}`]: true
      }
    })
  }
}

export default {
  name: 'PropertyStatistics',

  components: {
    LoadingSpinner,
    ChartContainer
  },

  data () {
    return {
      statistics: {},
      loading: true
    }
  },

  computed: {
    currencySymbol () { return currencyCodeToSymbol(this.activeCompany?.currency.iso) },
    occupancy () { return this.statistics.occupancy },
    occupancyData () {
      const labels = this.occupancy.map(item => item.month)
      const data = this.occupancy.map(item => item.percentage)
      return {
        labels,
        series: [data]
      }
    },
    occupancyOptions () {
      return {
        fullWidth: true,
        height: 210,
        high: 100,
        chartPadding: { right: 40 },
        axisY: {
          onlyInteger: true,
          labelInterpolationFnc: this.percentLabel
        }
      }
    },
    sources () { return this.statistics.booking_sources },
    sourceData () {
      const labels = []
      const series = this.sources.map(item => item.booked)
      return {
        labels,
        series
      }
    },
    sourceOptions () {
      const legendNames = this.sources.map((item) => {
        const bookingSource = item.source
        return this.$store.getters.propertyItemBookingSourceLabel({ bookingSource })
      })
      return {
        labelOffset: 32,
        height: 200,
        plugins: [],
        legendNames: legendNames
      }
    },
    rates () { return this.statistics.rates },
    rateData () {
      const data = this.rates.map(item => item.rate)
      const labels = this.rates.map(i => {
        if (isFirstDayOfMonth(i.date)) {
          return format(i.date, 'MMMM')
        }
        return null
      })
      return {
        labels,
        series: [data]
      }
    },
    rateOptions () {
      return {
        fullWidth: true,
        height: 210,
        lineSmooth: false,
        chartPadding: { right: 40 },
        showPoint: false,
        axisY: {
          low: 0,
          onlyInteger: true,
          labelInterpolationFnc: this.currencyLabel
        }
      }
    },
    revenue () { return this.statistics.booking_revenue },
    revenueData () {
      const labels = this.revenue.map(item => item.month)
      const data = this.revenue.map(item => item.amount)
      return {
        labels,
        series: [data]
      }
    },
    revenueOptions () {
      return {
        fullWidth: true,
        height: 210,
        chartPadding: { right: 40 },
        axisY: {
          onlyInteger: true,
          labelInterpolationFnc: this.currencyLabel
        }
      }
    },
    ...mapGetters([
      'activeCompanyId',
      'activeCompany',
      'activeLinkedOwnerId',
      'activeProperty',
      'lastBookedPropertyItem',
      'lastCancelledPropertyItem'
    ])
  },

  watch: {
    async lastBookedPropertyItem (item) {
      if (item.property_id === this.activeProperty.id) await this.getStatistics()
    },
    async lastCancelledPropertyItem (item) {
      if (item.property_id === this.activeProperty.id) await this.getStatistics()
    }
  },

  activated () {
    this.refreshCharts()
  },

  async created () {
    await this.getStatistics()
  },

  methods: {
    percentLabel (value) { return `${Math.round(value)}%` },
    currencyLabel (value) { return `${this.currencySymbol}${value}` },
    refreshCharts () {
      const chartRefs = [
        'rateChart',
        'revenueChart',
        'occupancyChart',
        'sourceChart'
      ]
      chartRefs.forEach(ref => {
        const chartRef = this.$refs[ref]
        if (typeof chartRef?.updateChart === 'function') {
          chartRef.updateChart()
        }
      })
    },
    async getStatistics () {
      this.loading = true
      try {
        const companyId = this.activeCompanyId
        const userId = this.activeLinkedOwnerId
        const propertyId = this.activeProperty.id
        const response = await ApiGateway.invokeApi({
          method: 'GET',
          pathTemplate: '/companies/{companyId}/owners/{userId}/properties/{propertyId}/statistics',
          params: {
            companyId,
            userId,
            propertyId
          }
        })
        this.statistics = response.data
      } catch (e) {
        Raven.captureException(e)
        this.$buefy.toast.open({
          message: `Error loading property statistics - ${getErrorMessage(e)}`,
          type: 'is-danger'
        })
      }
      this.loading = false
    }
  }
}
</script>

<style lang="sass">
.property-statistics
  .ct-legend.ct-legend-inside
    position: relative
  
  // Add basic chart styling
  .ct-chart
    height: 210px
    width: 100%
    margin-bottom: 1rem
    
  // Ensure pie charts render correctly with proper series colors
  .ct-chart-pie .ct-series
    path
      stroke: white
      stroke-width: 1px
      
  // Apply the custom series colors to match the legend
  .ct-chart .ct-series-a .ct-slice-pie,
  .ct-chart .ct-series-a .ct-slice-donut-solid,
  .ct-chart .ct-series-a .ct-area,
  .ct-chart .ct-series-a .ct-bar
    stroke: #c0e596
    fill: #c0e596
    
  .ct-chart .ct-series-a .ct-line,
  .ct-chart .ct-series-a .ct-point
    stroke: #c0e596
    fill: none
    
  .ct-chart .ct-series-b .ct-slice-pie,
  .ct-chart .ct-series-b .ct-slice-donut-solid,
  .ct-chart .ct-series-b .ct-area,
  .ct-chart .ct-series-b .ct-bar
    stroke: #7ebd35
    fill: #7ebd35
    
  .ct-chart .ct-series-b .ct-line,
  .ct-chart .ct-series-b .ct-point
    stroke: #7ebd35
    fill: none
    
  .ct-chart .ct-series-c .ct-slice-pie,
  .ct-chart .ct-series-c .ct-slice-donut-solid,
  .ct-chart .ct-series-c .ct-area,
  .ct-chart .ct-series-c .ct-bar
    stroke: #35bd74
    fill: #35bd74
    
  .ct-chart .ct-series-c .ct-line,
  .ct-chart .ct-series-c .ct-point
    stroke: #35bd74
    fill: none
    
  .ct-chart .ct-series-d .ct-slice-pie,
  .ct-chart .ct-series-d .ct-slice-donut-solid,
  .ct-chart .ct-series-d .ct-area,
  .ct-chart .ct-series-d .ct-bar
    stroke: #589fcc
    fill: #589fcc
    
  .ct-chart .ct-series-d .ct-line,
  .ct-chart .ct-series-d .ct-point
    stroke: #589fcc
    fill: none
    
  .ct-chart .ct-series-e .ct-slice-pie,
  .ct-chart .ct-series-e .ct-slice-donut-solid,
  .ct-chart .ct-series-e .ct-area,
  .ct-chart .ct-series-e .ct-bar
    stroke: #357ebd
    fill: #357ebd
    
  .ct-chart .ct-series-e .ct-line,
  .ct-chart .ct-series-e .ct-point
    stroke: #357ebd
    fill: none
    
  .ct-chart .ct-series-f .ct-slice-pie,
  .ct-chart .ct-series-f .ct-slice-donut-solid,
  .ct-chart .ct-series-f .ct-area,
  .ct-chart .ct-series-f .ct-bar
    stroke: #4e6a9a
    fill: #4e6a9a
    
  .ct-chart .ct-series-f .ct-line,
  .ct-chart .ct-series-f .ct-point
    stroke: #4e6a9a
    fill: none
    
  .ct-chart .ct-series-g .ct-slice-pie,
  .ct-chart .ct-series-g .ct-slice-donut-solid,
  .ct-chart .ct-series-g .ct-area,
  .ct-chart .ct-series-g .ct-bar
    stroke: #0544d3
    fill: #0544d3
    
  .ct-chart .ct-series-g .ct-line,
  .ct-chart .ct-series-g .ct-point
    stroke: #0544d3
    fill: none
    
  .ct-chart .ct-series-h .ct-slice-pie,
  .ct-chart .ct-series-h .ct-slice-donut-solid,
  .ct-chart .ct-series-h .ct-area,
  .ct-chart .ct-series-h .ct-bar
    stroke: #6b0392
    fill: #6b0392
    
  .ct-chart .ct-series-h .ct-line,
  .ct-chart .ct-series-h .ct-point
    stroke: #6b0392
    fill: none
    
  .ct-chart .ct-series-i .ct-slice-pie,
  .ct-chart .ct-series-i .ct-slice-donut-solid,
  .ct-chart .ct-series-i .ct-area,
  .ct-chart .ct-series-i .ct-bar
    stroke: #f05b4f
    fill: #f05b4f
    
  .ct-chart .ct-series-i .ct-line,
  .ct-chart .ct-series-i .ct-point
    stroke: #f05b4f
    fill: none
    
  .ct-chart .ct-series-j .ct-slice-pie,
  .ct-chart .ct-series-j .ct-slice-donut-solid,
  .ct-chart .ct-series-j .ct-area,
  .ct-chart .ct-series-j .ct-bar
    stroke: #dda458
    fill: #dda458
    
  .ct-chart .ct-series-j .ct-line,
  .ct-chart .ct-series-j .ct-point
    stroke: #dda458
    fill: none
    
  .ct-chart .ct-series-k .ct-slice-pie,
  .ct-chart .ct-series-k .ct-slice-donut-solid,
  .ct-chart .ct-series-k .ct-area,
  .ct-chart .ct-series-k .ct-bar
    stroke: #eacf7d
    fill: #eacf7d
    
  .ct-chart .ct-series-k .ct-line,
  .ct-chart .ct-series-k .ct-point
    stroke: #eacf7d
    fill: none
    
  .ct-chart .ct-series-l .ct-slice-pie,
  .ct-chart .ct-series-l .ct-slice-donut-solid,
  .ct-chart .ct-series-l .ct-area,
  .ct-chart .ct-series-l .ct-bar
    stroke: #86797d
    fill: #86797d
    
  .ct-chart .ct-series-l .ct-line,
  .ct-chart .ct-series-l .ct-point
    stroke: #86797d
    fill: none
    
  .ct-chart .ct-series-m .ct-slice-pie,
  .ct-chart .ct-series-m .ct-slice-donut-solid,
  .ct-chart .ct-series-m .ct-area,
  .ct-chart .ct-series-m .ct-bar
    stroke: #b2c326
    fill: #b2c326
    
  .ct-chart .ct-series-m .ct-line,
  .ct-chart .ct-series-m .ct-point
    stroke: #b2c326
    fill: none
    
  .ct-chart .ct-series-n .ct-slice-pie,
  .ct-chart .ct-series-n .ct-slice-donut-solid,
  .ct-chart .ct-series-n .ct-area,
  .ct-chart .ct-series-n .ct-bar
    stroke: #6188e2
    fill: #6188e2
    
  .ct-chart .ct-series-n .ct-line,
  .ct-chart .ct-series-n .ct-point
    stroke: #6188e2
    fill: none
    
  .ct-chart .ct-series-o .ct-slice-pie,
  .ct-chart .ct-series-o .ct-slice-donut-solid,
  .ct-chart .ct-series-o .ct-area,
  .ct-chart .ct-series-o .ct-bar
    stroke: #a748ca
    fill: #a748ca
    
  .ct-chart .ct-series-o .ct-line,
  .ct-chart .ct-series-o .ct-point
    stroke: #a748ca
    fill: none
  
  // Ensure bar charts render correctly
  .ct-chart-bar .ct-bar
    stroke-width: 20px
  
  // Ensure line charts render correctly
  .ct-chart-line
    .ct-line
      stroke-width: 2px
    .ct-point
      stroke-width: 8px
      
  // Legend styles
  .ct-legend
    margin-top: 1rem
    
    ul.ct-legend-items
      list-style: none
      padding: 0
      margin: 0
      display: flex
      flex-wrap: wrap
      justify-content: center
      
      li
        position: relative
        padding-left: 20px
        margin-right: 15px
        margin-bottom: 5px
        
        &:before
          content: ""
          width: 12px
          height: 12px
          position: absolute
          left: 0
          top: 4px
          border-radius: 2px
          
      // This will be overridden by the specific legend colors below
          
  // Override the legend colors to match Chartist's series colors exactly
  @for $i from 0 through 14
    .ct-legend .ct-series-#{$i}:before
      background-color: nth((#c0e596, #7ebd35, #35bd74, #589fcc, #357ebd, #4e6a9a, #0544d3, #6b0392, #f05b4f, #dda458, #eacf7d, #86797d, #b2c326, #6188e2, #a748ca), $i + 1)
</style>
